import {api} from 'nutshell-core/api';
import {convertToObj} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

/**
 * GET /rest/leads/installments/report — matches the products report URL shape
 * (`/rest/leads/products/report`).
 */
export const INSTALLMENTS_REPORT_PATH = 'leads/installments/report';
export const INSTALLMENTS_LIST_PATH = 'leads/installments/list';

export type InstallmentsListInstallment = {
    id: string;
    dueTime: number;
    value: number;
    isFailed: boolean;
    description: string | null;
};

export type InstallmentsListRow = {
    id: string;
    name: string;
    htmlUrlPath: string;
    primaryEntityName: string;
    startDate: number | null;
    status: string;
    totals: {
        collected: number;
        planned: number;
        // Backend omits `pipelines` when includeOpenLeads is false.
        pipelines?: number;
        failed: number;
        total: number;
    };
    installments: InstallmentsListInstallment[];
};

export type InstallmentsListApiResponse = {
    rows: InstallmentsListRow[];
    totalLeads: number;
    hasMore: boolean;
    page: number;
    limit: number;
    sort: string;
    facet: InstallmentsReportFacet;
};

export type InstallmentsReportFacet = 'total' | 'count';

export function parseIncludeOpenLeads(value: string | undefined): boolean {
    if (value === undefined || value === '') {
        return true;
    }
    return ['true', '1'].includes(String(value).toLowerCase());
}

export function parseInstallmentsFacet(value: unknown): InstallmentsReportFacet {
    if (value === 'count') {
        return 'count';
    }
    return 'total';
}

export type InstallmentsChartBucket = {
    key: string;
    label: string;
    startTime: number;
    endTime: number;
    collected: number;
    planned: number;
    failed: number;
    total: number;
    // Backend omits `pipelines` when includeOpenLeads is false; consumers
    // should treat its absence as "do not render a Pipelines series."
    pipelines?: number;
};

export type InstallmentsReportApiResponse = {
    summary: {
        facet: InstallmentsReportFacet;
        collected: number;
        planned: number;
        failed: number;
        total: number;
        pipelines?: number;
    };
    chart: {
        gap: string;
        facet: InstallmentsReportFacet;
        buckets: InstallmentsChartBucket[];
    };
    details: {
        facet: InstallmentsReportFacet;
        rows: Array<{
            label: string;
            collected: number;
            planned: number;
            failed: number;
            total: number;
            pipelines?: number;
        }>;
        totals: {
            collected: number;
            planned: number;
            failed: number;
            total: number;
            pipelines?: number;
        };
    };
    filtersApplied: {
        gap: string;
        facet: InstallmentsReportFacet;
        includeOpenLeads: boolean;
    };
};

export function fetchInstallmentsReport(options: {
    activeFilters: FilterObject[];
    gap: string;
    facet?: InstallmentsReportFacet;
    includeOpenLeads?: boolean;
    signal?: AbortSignal;
}): Promise<InstallmentsReportApiResponse> {
    const {activeFilters, gap, facet = 'total', includeOpenLeads = false, signal} = options;
    return api
        .get(
            INSTALLMENTS_REPORT_PATH,
            {
                filter: convertToObj(activeFilters),
                gap,
                facet,
                includeOpenLeads,
            },
            signal ? {signal} : undefined
        )
        .then((response: unknown) => (response as Response).json());
}

export function fetchInstallmentsList(options: {
    activeFilters: FilterObject[];
    page: number;
    limit: number;
    sort: string;
    facet?: InstallmentsReportFacet;
    includeOpenLeads?: boolean;
    signal?: AbortSignal;
}): Promise<InstallmentsListApiResponse> {
    const {
        activeFilters,
        page,
        limit,
        sort,
        facet = 'total',
        includeOpenLeads = false,
        signal,
    } = options;
    return api
        .get(
            INSTALLMENTS_LIST_PATH,
            {
                filter: convertToObj(activeFilters),
                page,
                limit,
                sort,
                facet,
                includeOpenLeads,
            },
            signal ? {signal} : undefined
        )
        .then((response: unknown) => (response as Response).json());
}
